import { z } from 'zod';
import { IdeationAudienceScoreListSchema, IdeationCreativeScoreListSchema, IdeationIdeaWithImagesSchema, IdeationWidgetSchema, } from '../ideation/ideation.schema';
import { OpportunityRegionSchema } from './opportunity-region.schema';
import { UnifiedSourceSchema } from './social-opportunities.data.schema';
// The opportunity pipeline may skip idea scoring; ideation's own schema stays strict.
export const OpportunityIdeationIdeaSchema = IdeationIdeaWithImagesSchema.extend({
    audienceScore: IdeationAudienceScoreListSchema.optional(),
    creativeScore: IdeationCreativeScoreListSchema.optional(),
});
export const OpportunityIdeationWidgetSchema = IdeationWidgetSchema.extend({
    ideas: z.array(OpportunityIdeationIdeaSchema).min(1),
});
export const InsightTypeValues = [
    'social',
    'performance',
    'competition',
    'brand_product',
    'strategic',
];
export const InsightTypeSchema = z.enum(InsightTypeValues);
export const insightReasoning = z.object({
    title: z
        .string()
        .describe('Specific, descriptive title indicating the strategic angle of this reasoning (8-15 words). ' +
        'Should clearly convey what aspect of value or alignment this reasoning addresses.'),
    description: z
        .string()
        .describe('Persuasive explanation (2-5 sentences) that: 1) Makes specific connections to brand products/keywords/domains, ' +
        '2) References concrete goals/KPIs/objectives, 3) Explains audience relevance, and 4) Articulates tangible business impact.'),
    sourceKeys: z
        .array(z.string())
        .describe('Array of source keys that support this reasoning. References sources in the sources object.'),
});
export const brandMention = z.object({
    sourceKey: z
        .string()
        .describe('Source key that references the sources field. Example: "post_123", "post_456".'),
    terms: z
        .array(z.string())
        .describe('Array of words, phrases, hashtags, or brands being tracked for this source. Example: ["Chevy Blazer", "family support", "#EV"].'),
});
export const AudienceProfileSchema = z.object({
    id: z
        .string()
        .optional()
        .describe('Unique identifier of the audience/persona. Example: "persona_abc123".'),
    name: z
        .string()
        .describe('Name of the audience segment. Example: "Young Parents", "Tech-Savvy Millennials".'),
    personaTitle: z
        .string()
        .optional()
        .describe('Short persona title without a personal name. Example: "Safety-conscious suburban parent".'),
    segmentName: z
        .string()
        .describe('Group categorization name for the demographic. Example: "Young Professionals", "Budget-Conscious Parents".'),
    age: z.number().nullable().optional().describe('Age of the persona. Example: 28.'),
    role: z
        .string()
        .optional()
        .describe('Role or occupation of the persona. Example: "Content Creator", "Marketing Manager".'),
    age_range: z.string().describe('Age range of the audience. Example: "25-34", "18-24".'),
    interests: z
        .string()
        .describe('Interests and hobbies of the audience. Example: "Technology, gaming, social media".'),
    location: z.string().describe('Geographic location of the audience. Example: "Urban areas, US".'),
    behaviors: z
        .string()
        .describe('Behavioral characteristics and patterns of the audience. Example: "Active on social media, early adopters".'),
    quote: z
        .string()
        .optional()
        .describe('Quote from a social media post or comment that exemplifies this audience\'s perspective, needs, or sentiment related to this opportunity. Example: "As a young parent, I need a car that keeps my kids safe while I\'m focused on the road." Provides authentic voice and context for marketing teams.'),
    quoteConfidence: z
        .enum(['high', 'medium', 'low'])
        .optional()
        .describe('Confidence level of the quote relevance and authenticity. High: direct quote strongly aligned with opportunity. Medium: paraphrased or moderately aligned. Low: inferred or loosely connected.'),
    styleProfile: z
        .object({
        toneOfVoice: z
            .string()
            .optional()
            .describe('Tone of voice of the audience. Example: "Warm and caring, practical and straightforward, reassuring without being condescending".'),
        communicationStyle: z
            .string()
            .optional()
            .describe('Communication style of the audience. Example: "Clear and empathetic, values practical examples and real-life applications, appreciates honesty and transparency, prefers substance over flashy marketing".'),
        writingStyle: z
            .string()
            .optional()
            .describe('Writing style of the audience. Example: "Straightforward and organized, uses relatable examples, focuses on practical benefits, warm personal touch, accessible language without excessive jargon".'),
        speakingStyle: z
            .string()
            .optional()
            .describe('Speaking style of the audience. Example: "Articulate and compassionate, good listener, asks clarifying questions about family implications, shares relevant personal experiences, maintains professional yet approachable demeanor".'),
    })
        .optional()
        .describe('Communication and speaking style preferences of the audience.'),
    photoAssetId: z
        .string()
        .nullable()
        .optional()
        .describe('Asset ID for the persona image. Used to fetch the image from the assets service.'),
});
export const engagementBreakdown = z
    .object({
    likes: z.number().catch(-1).describe('Number of likes. Example: 1200.'),
    comments: z.number().catch(-1).describe('Number of comments. Example: 300.'),
    replies: z.number().catch(-1).describe('Number of replies (nested comments). Example: 100.'),
    shares: z.number().catch(-1).describe('Number of shares. Example: 150.'),
    views: z.number().catch(-1).describe('Number of views. Example: 50000.'),
    total: z
        .number()
        .catch(-1)
        .describe('Total engagement (sum of all interactions). Example: 52050.'),
})
    .describe('Consolidated engagement metrics');
export var EngagementVelocity;
(function (EngagementVelocity) {
    EngagementVelocity["peaking"] = "peaking";
    EngagementVelocity["accelerating"] = "accelerating";
    EngagementVelocity["plateauing"] = "plateauing";
    EngagementVelocity["declining"] = "declining";
})(EngagementVelocity || (EngagementVelocity = {}));
export const engagementVelocitySchema = z
    .enum([
    EngagementVelocity.peaking,
    EngagementVelocity.accelerating,
    EngagementVelocity.plateauing,
    EngagementVelocity.declining,
])
    .describe('Engagement momentum. Indicates the current momentum of engagement for the opportunity, supporting marketing timing and strategy decisions.');
export const opportunityEngagement = z.object({
    breakdown: engagementBreakdown,
    rate: z
        .number()
        .catch(-1)
        .describe('Overall engagement rate. Example: 0.108. Calculated as: (likes + comments + replies + shares) / views. High: >0.05 (5%+), Medium: 0.02-0.05 (2-5%), Low: <0.02 (<2%).'),
    velocity: engagementVelocitySchema,
    velocityRate: z
        .number()
        .catch(-1)
        .describe('Quantified rate of change for engagement velocity over time. Positive values indicate increasing engagement momentum, negative values indicate declining momentum. Example: 0.15 (15% increase), -0.08 (8% decrease). Helps marketers understand the speed and direction of trend changes for optimal campaign timing.'),
});
export const topVoice = z.object({
    username: z.string().describe('Voice username. Example: "influencer1".'),
    postURL: z.string().describe('Post URL. Example: "https://...".'),
});
export const brandInfoSchema = z.object({
    brandName: z.string().describe('The name of the brand according to the internal documents.'),
    product: z
        .object({
        name: z.string(),
        summary: z.string(),
        keywords: z
            .string()
            .array()
            .describe("The top keywords describing the product's features, capabilities, and value proposition."),
    })
        .array()
        .describe('The product or products belonging to the brand detailed within the internal documents.'),
    domain: z.array(z.string()).describe('List of industries and domains the brand operates in.'),
});
export const thumbnailSchema = z.object({
    assetId: z.string().describe('ID of the generated thumbnail image asset'),
});
export const evidenceItemSchema = z.object({
    claim: z.string().describe('Specific claim or finding supporting the score'),
    dataPoint: z
        .string()
        .describe('Exact data point, quote, or metric from the input that supports this claim'),
});
export const scoreSchema = z
    .object({
    value: z
        .number()
        .min(1)
        .max(5)
        .describe('The score of the opportunity from 1-5 (1=Poor, 2=Weak, 3=Moderate, 4=Strong, 5=Excellent)'),
    reasoning: z
        .string()
        .describe('2-3 sentence explanation grounded in specific evidence from the provided data'),
    evidence: z
        .array(evidenceItemSchema)
        .min(0)
        .max(5)
        .describe('Array of up to 5 specific evidence items with claims and data points that support the score'),
})
    .nullable();
export const durationTypeSchema = z
    .object({
    value: z
        .enum(['flash', 'seasonal', 'behavioral', 'evergreen'])
        .describe('The duration type of the opportunity. One of: flash (short-term, momentum-driven), seasonal (predictable, recurring), behavioral (long-term behavior change), evergreen (consistent relevance).'),
    reasoning: z
        .string()
        .describe('2-3 sentence explanation grounded in specific evidence from the provided data that justifies the assigned duration type.'),
    impact: z
        .string()
        .describe("2-3 sentence analysis of how this duration type classification affects the opportunity's potential and recommended marketing approach."),
})
    .nullable();
export const overallScoreMetadataSchema = z
    .looseObject({})
    .describe('Metadata about how the overall score was computed. Contains score modifiers like recency_multiplier, uniqueness_bonus, trend_penalty, and their explanations.');
export const overallScoreSchema = z.object({
    value: z.number(),
    metadata: overallScoreMetadataSchema.describe('Object containing information about score modifiers and how the score was computed'),
});
export const ScoresSchema = z.object({
    brandFit: scoreSchema,
    productCategoryFit: scoreSchema,
    audienceFit: scoreSchema,
    trendRelevance: scoreSchema,
    trendBreadth: scoreSchema,
    postQuality: scoreSchema,
    overallScore: overallScoreSchema.nullable().optional(),
});
export const SocialOpportunityItemSchema = z.object({
    opportunityId: z
        .string()
        .describe('Unique identifier for the opportunity. Example: "opp_12345". Used to track and reference the opportunity in marketing analytics and reporting.'),
    createdAt: z.string().describe('Timestamp when the opportunity was created'),
    opportunityTitle: z
        .string()
        .describe('Title of the opportunity. Example: "Viral TikTok Trend". Grabs attention and communicates the core of the opportunity in summary cards.'),
    opportunitySummary: z
        .string()
        .describe('Short opportunity description (max 150 characters). Example: "New TikTok dance trend gaining momentum among Gen Z users with 2M+ views". Provides a concise overview for quick scanning in summary cards and dashboards.'),
    opportunityDescription: z
        .string()
        .describe('Description of the opportunity. Example: "A new dance challenge is trending among Gen Z on TikTok." Provides narrative context in the detailed view to help users understand the opportunity in depth.'),
    opportunityCategories: z
        .array(z.string())
        .describe('LLM-generated categories for filtering. Example: ["Autonomy", "Community", "Safety"]. Used to organize or filter opportunities by strategic focus areas like autonomy, community, safety.'),
    insightType: InsightTypeSchema.optional().describe('Strategic insight classification: social (community/cultural trends), performance (metrics/ROI focus), ' +
        'competition (competitive intelligence), brand_product (brand positioning/product features), ' +
        'strategic (long-term planning/market positioning). Used for filtering and prioritizing opportunities ' +
        'based on strategic marketing focus.'),
    trendTitle: z
        .string()
        .describe('Title that represents the trend. Example: "Gen Z Dance Challenge". Clearly explains the trend in a short title.'),
    trendDescription: z
        .string()
        .describe('Description of the trend which will provide the reader more details and context to the trend. Example: "A new dance challenge is trending among Gen Z on TikTok." Provides narrative context in the detailed view to help users understand the trend in depth.'),
    trendInsight: z
        .string()
        .describe("Summary of the key insight for this opportunity, often highlighting trending terms or conversation themes with specific metrics. Example: \"Terms like 'family-safe tech', 'kids in the backseat' and 'hands-free driving with kids' have surged 20%.\" Used to quickly communicate the main finding to marketers."),
    insightReasonings: z
        .array(insightReasoning)
        .describe('Array of reasonings explaining why the insight was generated. Provides context and justification for the insight to support marketing decisions.'),
    actionableInsights: z
        .string()
        .describe('Specific actionable recommendations for marketers. Example: "Create a campaign or social content series featuring real Chevy Blazer owners sharing how the vehicle supports their families, especially in challenging times. Integrate themes of community, resilience, and advanced technology." Provides concrete next steps and campaign ideas that marketing teams can execute.'),
    sources: z
        .record(z.string().describe('Source key'), UnifiedSourceSchema)
        .describe('Normalized source data indexed by source key - eliminates duplication'),
    brandMentions: z
        .array(brandMention)
        .describe('Brand mentions grouped by source with all terms for each source. Example: [{ sourceKey: "post_123", terms: ["Nike", "sports"] }, { sourceKey: "post_456", terms: ["family support", "community"] }]. Quantifies how frequently brands or topics appear per source—signals source relevance. Used for needs aggregation and brand reputation analysis.'),
    engagement: opportunityEngagement.describe('Consolidated engagement data'),
    topVoices: z
        .array(topVoice)
        .describe('Highlights influential users or sources to validate trend importance and identify potential partners or advocates.'),
    thumbnail: thumbnailSchema.optional().describe('Generated opportunity thumbnail.'),
    exportAssetId: z
        .string()
        .optional()
        .describe('Saved export asset ID for the opportunity PDF download (if available).'),
    priorityReference: z.string().describe('Priority reference'),
    priorityReasoning: z
        .string()
        .describe('Reasoning for priority. Example: "High engagement and growth rate".'),
    targetAudience: z
        .string()
        .array()
        .describe('Array of detected target audience descriptions. Example: "Gen Z, Millennials".'),
    targetAudienceProfiles: z
        .array(AudienceProfileSchema)
        .optional()
        .describe('Array of target audience profiles.'),
    audienceReasoning: z
        .string()
        .describe('Reasoning for audience selection. Example: "Most mentions from Gen Z demographics".'),
    predictedOutcome: z
        .string()
        .describe('Predicted outcome. Example: "Increase in brand awareness".'),
    targetedGoals: z.string().array().optional(),
    brandInfo: brandInfoSchema.optional(),
    scores: ScoresSchema.optional().describe('Quality scores evaluating the opportunity across multiple dimensions. ' +
        'Used for filtering, ranking, and assessing opportunity quality.'),
    durationType: durationTypeSchema
        .optional()
        .describe('Duration-type classification of the opportunity based on its characteristics and potential impact.'),
    region: OpportunityRegionSchema.optional(),
    ideationData: OpportunityIdeationWidgetSchema.nullish().describe('Creative ideation data generated for this opportunity, including creative ideas, strategic elements, and executions'),
});
export const SocialOpportunitiesWidgetKey = 'social-opportunities-widget:v1';
export const SocialOpportunitiesWidgetKind = 'social_opportunities';
/**
 * Key prefix for a saved-opportunity artifact: `${prefix}${opportunityId}`.
 * The api mints the artifact under this key; the Library reads it back by
 * matching the prefix to route to the rich opportunity preview. Shared here so
 * the producing (api) and consuming (agent-studio) sides can never drift.
 */
export const SOCIAL_OPPORTUNITY_ARTIFACT_KEY_PREFIX = 'social-opportunity:';
export const SocialOpportunitiesWidgetSchema = z.object({
    brandName: z
        .string()
        .optional()
        .describe('Brand name to focus on for social media opportunities'),
    items: z.array(SocialOpportunityItemSchema).describe('List of social opportunities'),
});
